import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatIDR, fileUrl } from "../lib/api";
import StockPill, { DRAFT_PILL } from "../components/StockPill";
import JubelioBadge from "../components/JubelioBadge";
import { useAuth } from "../context/AuthContext";
import { ReviewBadge, reviewOf } from "../lib/review";
import { useBrand } from "../context/BrandContext";
import { canEdit } from "../lib/roles";
import { Plus, MagnifyingGlass, Eye, PencilSimple, Funnel, X, Trash } from "@phosphor-icons/react";

const STATUSES = [
    { value: "all", label: "All status" },
    { value: "active", label: "Active" },
    { value: "draft", label: "Draft" },
    { value: "archived", label: "Archived" },
];
const STOCK_STATES = [
    { value: "all", label: "All stock" },
    { value: "in_stock", label: "Available" },
    { value: "low_stock", label: "Low stock" },
    { value: "out_of_stock", label: "Out of stock" },
];

/**
 * Operational Product Master.
 *
 * Catalog (visual, selling view) is a separate page. This Products page is the
 * compact database view optimised for scanning long lists, filtering, and
 * jumping straight into EDIT for users with edit permission. Admin Sales lands
 * on the read-only product detail.
 */
export default function Products() {
    const { user } = useAuth();
    const { current: activeBrand } = useBrand();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const editor = canEdit(user?.role);
    const isOwner = user?.role === "owner";

    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [groups, setGroups] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters are rehydrated from the URL so returning from Edit Product
    // (via navigate(returnTo) or the Back link) restores exactly the same
    // state. The `returnTo` passed to Edit Product includes these params.
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [cat, setCat] = useState(searchParams.get("cat") || "all");
    const [region, setRegion] = useState(searchParams.get("region") || "all");
    const [collection, setCollection] = useState(searchParams.get("collection") || "all");
    const [status, setStatus] = useState(searchParams.get("status") || "all");
    const [stock, setStock] = useState(searchParams.get("stock") || "all");
    const [productBrand, setProductBrand] = useState(searchParams.get("pb") || "all");
    const [review, setReview] = useState(() => new Set((searchParams.get("review") || "").split(",").filter(Boolean)));
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    // Permanent delete (Owner only). Reuses the shared `selectedIds` state.
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [singleDeleteId, setSingleDeleteId] = useState(null);

    const isHeelsAndTea = activeBrand === "heelsandtea";

    // Mirror filter state back to the URL (replace, no history noise). This
    // powers the "preserve filters on return" behavior.
    useEffect(() => {
        const next = new URLSearchParams();
        if (q) next.set("q", q);
        if (cat !== "all") next.set("cat", cat);
        if (region !== "all") next.set("region", region);
        if (collection !== "all") next.set("collection", collection);
        if (status !== "all") next.set("status", status);
        if (stock !== "all") next.set("stock", stock);
        if (productBrand !== "all") next.set("pb", productBrand);
        if (review.size) next.set("review", [...review].join(","));
        setSearchParams(next, { replace: true });
    }, [q, cat, region, collection, status, stock, productBrand, review, setSearchParams]);

    // Reset filters only when the user actively switches brands — never on
    // the initial mount (that would wipe URL-rehydrated filters).
    const prevBrandRef = useRef(activeBrand);
    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                const [p, c] = await Promise.all([
                    api.get("/products", { params: { brand: activeBrand } }),
                    api.get("/categories", { params: { brand: activeBrand } }),
                ]);
                setItems(p.data);
                setCats((c.data || []).filter((x) => !x.archived));
                if (isHeelsAndTea) {
                    try {
                        const s = await api.get("/catalog/structure", { params: { brand: activeBrand } });
                        setGroups((s.data?.groups || []).filter((g) => !g.archived));
                        setFeatured(s.data?.featured || []);
                    } catch {}
                } else {
                    setGroups([]);
                    setFeatured([]);
                }
            } finally {
                setLoading(false);
            }
        })();
        if (prevBrandRef.current !== activeBrand) {
            setCat("all"); setRegion("all"); setCollection("all");
            setStatus("all"); setStock("all"); setProductBrand("all");
            prevBrandRef.current = activeBrand;
        }
    }, [activeBrand, isHeelsAndTea]);

    const regions = useMemo(() => groups.filter((g) => g.kind === "region"), [groups]);
    const collections = useMemo(() => groups.filter((g) => g.kind === "collection"), [groups]);
    const productBrandOptions = useMemo(() => {
        const set = new Set();
        items.forEach((p) => { if (p.product_brand) set.add(p.product_brand); });
        return Array.from(set).sort();
    }, [items]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return items.filter((p) => {
            if (needle) {
                const hay = `${p.name} ${p.sku || ""}`.toLowerCase();
                if (!hay.includes(needle)) return false;
            }
            if (cat !== "all" && p.category !== cat) return false;
            if (region !== "all" && p.region !== region) return false;
            if (collection !== "all" && !(p.collections || []).includes(collection)) return false;
            if (status !== "all" && (p.status || "active") !== status) return false;
            if (stock !== "all" && p.stock_status !== stock) return false;
            if (productBrand !== "all" && p.product_brand !== productBrand) return false;
            if (review.size && !review.has(reviewOf(p))) return false;
            return true;
        });
    }, [items, q, cat, region, collection, status, stock, productBrand, review]);

    const activeFilterCount =
        (cat !== "all") + (region !== "all") + (collection !== "all") +
        (status !== "all") + (stock !== "all") + (productBrand !== "all");

    const resetFilters = () => {
        setCat("all"); setRegion("all"); setCollection("all");
        setStatus("all"); setStock("all"); setProductBrand("all");
    };

    // --- Bulk activation helpers ---
    // For Owner, `selectableIds` includes every visible row because Owners can
    // also permanently delete active products. For admin_input (editor but not
    // owner), the current activate-only convention is kept: only Draft /
    // Archived rows are selectable. Admin_sales is not an editor at all and
    // therefore sees no checkboxes.
    const selectableIds = useMemo(
        () =>
            isOwner
                ? filtered.map((p) => p.id)
                : filtered
                      .filter((p) => (p.status || "active") !== "active")
                      .map((p) => p.id),
        [filtered, isOwner]
    );
    const allVisibleSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
    const toggleOne = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };
    const toggleAllVisible = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) selectableIds.forEach((id) => next.delete(id));
            else selectableIds.forEach((id) => next.add(id));
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());
    const selectedList = useMemo(
        () => filtered.filter((p) => selectedIds.has(p.id)),
        [filtered, selectedIds]
    );
    const skippableBefore = selectedList.filter((p) => !(p.category || "").trim()).length;
    const runBulkActivate = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/products/bulk-activate", { product_ids: ids });
            const msg = `${data.activated_count} products activated. ${data.skipped_count} products skipped.`;
            if (data.skipped_count) {
                // Pop a toast with reasons for the first few skipped items.
                const reasons = (data.skipped || []).slice(0, 5)
                    .map((s) => `${s.sku || s.id}: ${s.reason}`).join("\n");
                // Lightweight: still show a success toast, then one info toast with reasons.
                import("sonner").then((m) => {
                    m.toast.success(msg);
                    m.toast.message("Skipped details", { description: reasons });
                });
            } else {
                import("sonner").then((m) => m.toast.success(msg));
            }
            // Refresh the list so newly-active products reflect their state.
            const query = {};
            if (activeBrand) query.brand = activeBrand;
            const { data: fresh } = await api.get("/products", { params: query });
            setItems(fresh);
            clearSelection();
        } catch (ex) {
            import("sonner").then((m) =>
                m.toast.error(ex?.response?.data?.detail || "Bulk activation failed")
            );
        } finally {
            setBulkBusy(false);
            setBulkConfirmOpen(false);
        }
    };

    // Permanent delete (Owner only). Single and bulk reuse the SAME backend
    // service (_delete_product_core) so there is only one deletion engine.
    const openSingleDelete = (pid) => {
        setSingleDeleteId(pid);
        setDeleteConfirmOpen(true);
    };
    const openBulkDelete = () => {
        setSingleDeleteId(null);
        setDeleteConfirmOpen(true);
    };
    const deleteTargets = useMemo(() => {
        if (singleDeleteId) return filtered.filter((p) => p.id === singleDeleteId);
        return selectedList;
    }, [singleDeleteId, filtered, selectedList]);
    const runPermanentDelete = async () => {
        const ids = deleteTargets.map((p) => p.id);
        if (!ids.length) return;
        setDeleteBusy(true);
        try {
            const { data } = await api.post("/products/bulk-delete", { product_ids: ids });
            const msg = `${data.deleted_count} product(s) permanently deleted. ${data.mappings_removed_total} Jubelio mapping(s) removed.`;
            import("sonner").then((m) => m.toast.success(msg));
            // Refresh list; drop selection and single-delete id.
            const query = {};
            if (activeBrand) query.brand = activeBrand;
            const { data: fresh } = await api.get("/products", { params: query });
            setItems(fresh);
            clearSelection();
            setSingleDeleteId(null);
        } catch (ex) {
            import("sonner").then((m) =>
                m.toast.error(ex?.response?.data?.detail || "Permanent delete failed")
            );
        } finally {
            setDeleteBusy(false);
            setDeleteConfirmOpen(false);
        }
    };

    const openProduct = (p) => {
        const returnTo = `${location.pathname}${location.search}`;
        if (editor) navigate(`/products/${p.id}/edit`, { state: { returnTo } });
        else navigate(`/products/${p.id}`, { state: { returnTo } });
    };

    return (
        <div className="animate-fade-up w-full max-w-full">
            <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
                <div>
                    <div className="eyebrow">Products</div>
                    <h1 className="font-serif text-4xl md:text-5xl mt-1 tracking-tight">
                        {isHeelsAndTea ? "Heels & Tea" : "Hoshizora"} · Product Database
                    </h1>
                    <p className="text-xs text-neutral-500 mt-2 max-w-xl">
                        Operational view. Click any product to edit. For the visual browsing experience use Catalog.
                    </p>
                </div>
                {editor && (
                    <Link
                        to="/products/new"
                        state={{ returnTo: `${location.pathname}${location.search}` }}
                        data-testid="products-add-button"
                        className="inline-flex items-center gap-1 bg-sage-dark text-white px-3 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep"
                    >
                        <Plus size={14} weight="bold" /> New Product
                    </Link>
                )}
            </div>

            {/* Filters */}
            <div className="mb-4 space-y-3">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <MagnifyingGlass size={16} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by product name or SKU…"
                            data-testid="products-search"
                            className="w-full border border-mist rounded-sm pl-9 pr-3 py-2 bg-white text-base md:text-sm focus:outline-none focus:border-sage-dark"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setFiltersOpen((o) => !o)}
                        data-testid="products-filters-toggle"
                        className={`inline-flex items-center gap-1.5 border rounded-sm px-3 py-2 text-xs uppercase tracking-widest ${filtersOpen || activeFilterCount ? "border-sage-dark text-sage-dark" : "border-mist text-neutral-600 hover:border-sage-dark"}`}
                    >
                        <Funnel size={14} weight="light" /> Filters
                        {activeFilterCount > 0 && <span className="ml-1 bg-sage-dark text-white rounded-full text-[10px] px-1.5">{activeFilterCount}</span>}
                    </button>
                </div>

                {/* Category chips — always visible, quick access */}
                <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                    <CategoryChip label="All categories" active={cat === "all"} onClick={() => setCat("all")} testId="cat-chip-all" />
                    {cats.map((c) => (
                        <CategoryChip key={c.id} label={c.name} active={cat === c.name} onClick={() => setCat(c.name)} testId={`cat-chip-${c.name}`} />
                    ))}
                </div>

                {editor && (
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="review-filter">
                        <span className="eyebrow !text-[11px] shrink-0">Review</span>
                        <CategoryChip label="All" active={review.size === 0} onClick={() => setReview(new Set())} testId="review-chip-all" />
                        {[["TO_CHECK", "To Check"], ["RECHECK", "Recheck"], ["CHECKED", "Checked"]].map(([k, l]) => (
                            <CategoryChip key={k} label={`${l} (${items.filter((p) => reviewOf(p) === k).length})`} active={review.has(k)} testId={`review-chip-${k}`}
                                onClick={() => setReview((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; })} />
                        ))}
                    </div>
                )}

                {/* Collapsible advanced filters */}
                {filtersOpen && (
                    <div className="border border-mist rounded-sm bg-white p-3 md:p-4 grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3" data-testid="products-filters-panel">
                        {isHeelsAndTea && (
                            <Dropdown label="Region" value={region} onChange={setRegion}
                                options={[{ value: "all", label: "All regions" }, ...regions.map((r) => ({ value: r.slug, label: `${r.icon || ""} ${r.name}` }))]} testId="filter-region" />
                        )}
                        {isHeelsAndTea && collections.length > 0 && (
                            <Dropdown label="Collection" value={collection} onChange={setCollection}
                                options={[{ value: "all", label: "All collections" }, ...collections.map((c) => ({ value: c.slug, label: `${c.icon || ""} ${c.name}` }))]} testId="filter-collection" />
                        )}
                        <Dropdown label="Status" value={status} onChange={setStatus} options={STATUSES} testId="filter-status" />
                        <Dropdown label="Stock" value={stock} onChange={setStock} options={STOCK_STATES} testId="filter-stock" />
                        {productBrandOptions.length > 0 && (
                            <Dropdown label="Product brand" value={productBrand} onChange={setProductBrand}
                                options={[{ value: "all", label: "All brands" }, ...productBrandOptions.map((b) => ({ value: b, label: b }))]} testId="filter-product-brand" />
                        )}
                        <div className="col-span-2 md:col-span-5 flex justify-end">
                            <button
                                type="button"
                                onClick={resetFilters}
                                disabled={activeFilterCount === 0}
                                className="text-xs uppercase tracking-widest text-neutral-500 hover:text-sage-dark inline-flex items-center gap-1 disabled:opacity-40"
                            >
                                <X size={12} weight="light" /> Reset filters
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-neutral-400 py-24 text-center">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="text-sm text-neutral-500 py-24 text-center border border-mist rounded-sm bg-white">
                    No products match the current filters.
                </div>
            ) : (
                <>
                    <div className="mb-3 text-xs text-neutral-500">
                        {filtered.length} {filtered.length === 1 ? "product" : "products"}
                    </div>

                    {editor && selectableIds.length > 0 && (
                        <div className="mb-3 flex flex-wrap items-center gap-2 border border-mist rounded-sm bg-[#FAF9F6] px-3 py-2" data-testid="bulk-toolbar">
                            <button
                                type="button"
                                onClick={toggleAllVisible}
                                data-testid="bulk-select-all-visible"
                                className="text-xs uppercase tracking-widest border border-mist bg-white px-3 py-1 rounded-sm hover:border-sage-dark"
                            >
                                {allVisibleSelected ? "Deselect all visible" : `Select all visible (${selectableIds.length})`}
                            </button>
                            <span className="text-xs text-neutral-500" data-testid="bulk-selected-count">
                                {selectedIds.size} selected
                            </span>
                            {selectedIds.size > 0 && (
                                <button type="button" onClick={clearSelection} data-testid="bulk-clear"
                                    className="text-xs text-neutral-500 underline hover:text-neutral-700">Clear</button>
                            )}
                            <div className="ml-auto flex items-center gap-2">
                                <button type="button" onClick={() => setBulkConfirmOpen(true)} disabled={selectedIds.size === 0 || bulkBusy}
                                    data-testid="bulk-activate-open"
                                    className="inline-flex items-center gap-1 bg-sage-dark text-white px-3 py-1 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-50">
                                    Activate selected
                                </button>
                                {isOwner && (
                                    <button type="button" onClick={openBulkDelete} disabled={selectedIds.size === 0 || deleteBusy}
                                        data-testid="bulk-delete-open"
                                        className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1 rounded-sm text-xs uppercase tracking-widest hover:bg-red-700 disabled:opacity-50">
                                        <Trash size={12} weight="bold" />
                                        Delete selected
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Desktop table */}
                    <div className="hidden md:block border border-mist rounded-sm bg-white overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-mist bg-[#FAF9F6]">
                                    {editor && <th className="eyebrow font-normal px-3 py-3 w-8"></th>}
                                    {["", "Product", "SKU", "Category", "Price", "Stock", "Sync", ...(editor ? ["Review"] : []), ""].map((h, i) => (
                                        <th key={i} className="eyebrow !text-[11.5px] !text-neutral-700 font-semibold px-3 py-3 whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p) => {
                                    const main = p.images?.find((i) => i.is_main) || p.images?.[0];
                                    return (
                                        <tr
                                            key={p.id}
                                            onClick={() => openProduct(p)}
                                            data-testid={`products-row-${p.sku || p.id}`}
                                            className="border-b border-mist hover:bg-[#F9F8F5] cursor-pointer"
                                        >
                                            {editor && (
                                                <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                                                    {isOwner || (p.status || "active") !== "active" ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.has(p.id)}
                                                            onChange={(e) => toggleOne(p.id, e.target.checked)}
                                                            data-testid={`row-check-${p.sku || p.id}`}
                                                            className="accent-sage-dark"
                                                        />
                                                    ) : (
                                                        <span className="inline-block w-4 h-4" />
                                                    )}
                                                </td>
                                            )}
                                            <td className="px-3 py-2 w-16">
                                                <div className="w-12 h-12 bg-[#F6F5F2] overflow-hidden rounded-sm flex items-center justify-center p-1">
                                                    {main && <img src={fileUrl(main.path)} alt="" className="w-full h-full object-contain" loading="lazy" />}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="font-sans text-[14px] font-medium text-ink leading-snug">{p.name}</div>
                                                <div className="text-[12px] text-neutral-600 mt-0.5">
                                                    {p.category}
                                                    {isHeelsAndTea && p.region && <span> · {regions.find((r) => r.slug === p.region)?.name || p.region}</span>}
                                                    {isHeelsAndTea && (p.collections || []).length > 0 && <span> · {p.collections.join(", ")}</span>}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 font-mono text-[13px] text-neutral-800">{p.sku || "—"}</td>
                                            <td className="px-3 py-2 text-[13px] text-neutral-800">
                                                <div>{p.category}</div>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap tabular-nums text-[13px] font-medium text-ink">{formatIDR(p.retail_price)}</td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="tabular-nums text-[13px] font-semibold text-ink">{p.stock}</span>
                                                    <StockPill status={p.stock_status} ops />
                                                    {p.status === "draft" && (
                                                        <span
                                                            data-testid={`row-draft-${p.sku || p.id}`}
                                                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] uppercase tracking-widest font-medium ${DRAFT_PILL}`}
                                                        >
                                                            Draft
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2"><JubelioBadge product={p} size={14} /></td>
                                            {editor && <td className="px-3 py-2"><ReviewBadge product={p} testId={`review-${p.sku || p.id}`} /></td>}
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                    {editor && (
                                                        <>
                                                            <Link
                                                                to={`/products/${p.id}`}
                                                                state={{ returnTo: `${location.pathname}${location.search}` }}
                                                                data-testid={`row-preview-${p.sku || p.id}`}
                                                                title="Preview (read-only)"
                                                                className="p-1.5 text-neutral-400 hover:text-sage-dark rounded-sm"
                                                            >
                                                                <Eye size={14} weight="light" />
                                                            </Link>
                                                            <Link
                                                                to={`/products/${p.id}/edit`}
                                                                state={{ returnTo: `${location.pathname}${location.search}` }}
                                                                data-testid={`row-edit-${p.sku || p.id}`}
                                                                title="Edit product"
                                                                className="p-1.5 text-sage-dark hover:bg-sage-soft rounded-sm"
                                                            >
                                                                <PencilSimple size={14} weight="light" />
                                                            </Link>
                                                            {isOwner && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openSingleDelete(p.id)}
                                                                    data-testid={`row-delete-${p.sku || p.id}`}
                                                                    title="Permanently delete"
                                                                    className="p-1.5 text-neutral-400 hover:text-red-600 rounded-sm"
                                                                >
                                                                    <Trash size={14} weight="light" />
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                    {!editor && (
                                                        <Link
                                                            to={`/products/${p.id}`}
                                                            state={{ returnTo: `${location.pathname}${location.search}` }}
                                                            data-testid={`row-view-${p.sku || p.id}`}
                                                            className="text-xs underline text-neutral-600"
                                                        >
                                                            View
                                                        </Link>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile compact list */}
                    <div className="md:hidden space-y-2">
                        {filtered.map((p) => {
                            const main = p.images?.find((i) => i.is_main) || p.images?.[0];
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => openProduct(p)}
                                    data-testid={`products-row-mobile-${p.sku || p.id}`}
                                    className="border border-mist bg-white rounded-sm p-3 flex items-start gap-3 active:bg-[#F9F8F5]"
                                >
                                    {editor && (isOwner || (p.status || "active") !== "active") && (
                                        <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(p.id)}
                                                onChange={(e) => toggleOne(p.id, e.target.checked)}
                                                data-testid={`row-check-mobile-${p.sku || p.id}`}
                                                className="accent-sage-dark"
                                            />
                                        </div>
                                    )}
                                    <div className="w-14 h-14 bg-[#F6F5F2] overflow-hidden rounded-sm flex items-center justify-center p-1 shrink-0">
                                        {main && <img src={fileUrl(main.path)} alt="" className="w-full h-full object-contain" loading="lazy" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-serif text-sm leading-tight line-clamp-1">{p.name}</div>
                                        <div className="text-[11px] text-neutral-500 mt-0.5 line-clamp-1">
                                            {p.category}
                                            {isHeelsAndTea && p.region && <span> · {regions.find((r) => r.slug === p.region)?.name || p.region}</span>}
                                        </div>
                                        <div className="font-mono text-[11px] text-neutral-700 mt-0.5 flex items-center gap-1.5"><span className="truncate">{p.sku || "—"}</span><JubelioBadge product={p} size={11} />{editor && <ReviewBadge product={p} compact testId={`review-mobile-${p.sku || p.id}`} />}</div>
                                        <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                                            <div className="tabular-nums text-xs font-medium">{formatIDR(p.retail_price)}</div>
                                            <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                                <span className="tabular-nums text-[11px] text-neutral-500">Stock {p.stock}</span>
                                                <StockPill status={p.stock_status} ops />
                                                {p.status === "draft" && (
                                                    <span
                                                        data-testid={`row-draft-mobile-${p.sku || p.id}`}
                                                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-medium ${DRAFT_PILL}`}
                                                    >
                                                        Draft
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {bulkConfirmOpen && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bulk-confirm-modal">
                    <div className="bg-white rounded-sm shadow-lg max-w-md w-full p-6">
                        <h3 className="font-serif text-xl mb-1">Activate selected products?</h3>
                        <p className="text-sm text-neutral-700 mb-2">
                            <strong data-testid="bulk-confirm-count">{selectedList.length}</strong> Draft products will become Active and may become visible in the public Catalog.
                        </p>
                        {skippableBefore > 0 && (
                            <p className="text-xs text-amber-700 mb-3" data-testid="bulk-confirm-skip-notice">
                                {skippableBefore} will be skipped (missing a valid category). Set a category on those products first if you want them activated.
                            </p>
                        )}
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setBulkConfirmOpen(false)} disabled={bulkBusy}
                                data-testid="bulk-confirm-cancel"
                                className="border border-mist text-neutral-700 px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark disabled:opacity-60">
                                Cancel
                            </button>
                            <button type="button" onClick={runBulkActivate} disabled={bulkBusy}
                                data-testid="bulk-confirm-activate"
                                className="bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                                {bulkBusy ? "Activating…" : `Activate ${selectedList.length} products`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmOpen && isOwner && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
                    <div className="bg-white rounded-sm shadow-lg max-w-md w-full p-6">
                        <h3 className="font-serif text-xl mb-1">Permanently delete selected products?</h3>
                        <p className="text-sm text-neutral-700 mb-2">
                            You are about to permanently delete{" "}
                            <strong data-testid="delete-confirm-count">{deleteTargets.length}</strong>{" "}
                            selected product{deleteTargets.length === 1 ? "" : "(s)"}.
                        </p>
                        <p className="text-sm text-neutral-700 mb-2">
                            This will:
                        </p>
                        <ul className="text-sm text-neutral-700 mb-3 list-disc pl-5 space-y-0.5">
                            <li>remove the products from Emergent</li>
                            <li>remove their local Jubelio mappings, where applicable</li>
                            <li>remove them from Catalog</li>
                            <li>stop them from participating in Jubelio mapped synchronization</li>
                        </ul>
                        <p className="text-xs text-neutral-500 mb-2">
                            Products in Jubelio will NOT be deleted or modified.
                        </p>
                        <p className="text-xs text-red-700 font-medium mb-4">
                            This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => { setDeleteConfirmOpen(false); setSingleDeleteId(null); }} disabled={deleteBusy}
                                data-testid="delete-confirm-cancel"
                                className="px-3 py-1.5 text-xs uppercase tracking-widest border border-mist rounded-sm hover:border-sage-dark">
                                Cancel
                            </button>
                            <button type="button" onClick={runPermanentDelete} disabled={deleteBusy || deleteTargets.length === 0}
                                data-testid="delete-confirm-run"
                                className="px-3 py-1.5 text-xs uppercase tracking-widest bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50">
                                {deleteBusy ? "Deleting…" : `Delete ${deleteTargets.length} Product${deleteTargets.length === 1 ? "" : "s"} Permanently`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CategoryChip({ label, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs uppercase tracking-widest transition-colors ${
                active
                    ? "bg-sage-dark text-white border border-sage-dark"
                    : "border border-mist text-neutral-600 hover:border-sage-dark"
            }`}
        >
            {label}
        </button>
    );
}

function Dropdown({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="mt-1 w-full border border-mist rounded-sm px-2 py-2 bg-white text-sm focus:outline-none focus:border-sage-dark"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}
